import { type FC, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { skipToken } from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { useButtonClassName } from '../../../../components/Button/useButtonClassName'
import { type AccountSchedulingSettings, type ConsentSettings, EventTypeAccess } from '../../../../models/Scheduling'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { useConsentManualConfigs } from '../../../../components/Consent/useConsentManualConfigs'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetSchedulingEventTypesQuery } from '../../../../store/scheduling/scheduling'
import { useDownloadGlobalFileRequestQuery } from '../../../../store/media/file'
import { ReactComponent as ChevronBullet } from '../../../../assets/images/chevron-bullet.svg'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { BOOKING_BASE_URL } from '../../../Scheduling/shared/bookingBaseUrl'
import { brandColorName } from '../../../Scheduling/shared/schedulingDisplay'
import { PreviewLabel } from '../../../Scheduling/shared/PreviewLabel'
import { ConfirmDialog } from '../../../Scheduling/shared/ConfirmDialog'
import { StatusBanner } from '../../../Scheduling/shared/StatusBanner'
import { type AccountPageBanner, accountPageBanner, accountPageLiveCount } from '../../../Scheduling/shared/eventTypeHostState'
import { accountBookingPagePath } from '../../../Scheduling/shared/bookingNavigation'
import { type UpdateAccountSettings } from '../SchedulingSetupTab'
import { SettingsCard, SettingsRow, SettingsSection, SettingsToggle } from './SettingsSection'
import styles from './SchedulingSettings.module.scss'

interface SectionProps {
  settings: AccountSchedulingSettings
  readOnly: boolean
  isSaving?: boolean
  onUpdate: UpdateAccountSettings
}

const ACCOUNT_PAGE_URL_HINT = 'To customize booking URLs, reach out to your implementation manager at Zocks.'

interface AccountPageStatusProps {
  banner: AccountPageBanner | null
  liveCount: number | null
  readOnly: boolean
}

const manageLink = (label: string, openAdd = false) => (
  <NavLink to={accountBookingPagePath} state={openAdd ? { openAdd: true } : undefined} className='paragraph-small bold zocks-text-primary text-nowrap'>
    {label}
  </NavLink>
)

const AccountPageStatus: FC<AccountPageStatusProps> = ({ banner, liveCount, readOnly }) => {
  if (!banner) {
    return (
      <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-600'>
        {liveCount != null ? <span>{`${liveCount} event type${liveCount === 1 ? ' is' : 's are'} live on this page.`}</span> : null}
        {manageLink('Manage event types')}
      </div>
    )
  }
  if (banner.kind === 'empty') {
    return (
      <StatusBanner tone='warning' action={readOnly ? manageLink('Manage event types') : manageLink('Add event types', true)}>
        This page has no event types. Clients can&apos;t book anything here.
      </StatusBanner>
    )
  }
  if (banner.kind === 'none-live') {
    return (
      <StatusBanner tone='danger' action={manageLink('Manage event types')}>
        No event types are live on this page. Clients can&apos;t book anything here.
      </StatusBanner>
    )
  }
  return (
    <StatusBanner tone='warning' action={manageLink('Manage event types')}>
      {`${banner.notLiveCount} of ${banner.total} event types on this page aren't live. Clients can't book them.`}
    </StatusBanner>
  )
}

const DEFAULT_CONSENT_CHECKBOX_TEXT = 'I consent to Zocks transcribing and analyzing this meeting with AI to create notes.'

const MAX_CONSENT_TEXT_LENGTH = 500

const ConsentMessageEditor: FC<{
  savedText: string | null | undefined
  readOnly: boolean
  onSave: (text: string | null) => void
}> = ({ savedText, readOnly, onSave }) => {
  const savedEffective = (savedText ?? '').trim() || DEFAULT_CONSENT_CHECKBOX_TEXT
  const [draft, setDraft] = useState(savedEffective)
  useEffect(() => {
    setDraft(savedEffective)
  }, [savedEffective])

  if (readOnly) {
    return <div className='paragraph-medium zocks-text-gray-700 zocks-px-0.75 py-2 rounded-3 zocks-bg-color-gray-100'>{savedEffective}</div>
  }

  const remainingCharacters = MAX_CONSENT_TEXT_LENGTH - draft.length
  const dirty = draft.trim() !== savedEffective
  const isAtDefault = draft.trim() === DEFAULT_CONSENT_CHECKBOX_TEXT
  const saveDraft = () => {
    const cleaned = draft.trim()
    onSave(!cleaned || cleaned === DEFAULT_CONSENT_CHECKBOX_TEXT ? null : cleaned)
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <div className='d-flex flex-column zocks-gap-0.25'>
        <label className='paragraph-small zocks-text-gray-900' htmlFor='scheduling-consent-message'>
          Message
        </label>
        <Form.Control
          as='textarea'
          id='scheduling-consent-message'
          rows={3}
          value={draft}
          maxLength={MAX_CONSENT_TEXT_LENGTH}
          onChange={(event) => setDraft(event.currentTarget.value)}
        />
        <div
          className={classNames('paragraph-extra-small text-end', {
            'zocks-text-gray-600': remainingCharacters > 0,
            'zocks-text-danger-200': remainingCharacters === 0,
          })}
        >
          {remainingCharacters} characters left
        </div>
      </div>
      <div className='d-flex align-items-center justify-content-between gap-3'>
        <Button
          variant='link'
          className={classNames('p-1 button-small fw-bold text-decoration-none', isAtDefault ? 'zocks-text-gray-500' : 'zocks-text-primary')}
          disabled={isAtDefault}
          onClick={() => setDraft(DEFAULT_CONSENT_CHECKBOX_TEXT)}
        >
          Reset to default
        </Button>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <Button variant='grayDarkGhostButton' onClick={() => setDraft(savedEffective)} className='zocks-py-0.25 zocks-px-0.75'>
            Cancel
          </Button>
          <Button variant='primary' disabled={!dirty} onClick={saveDraft} className='zocks-py-0.25 zocks-px-0.75'>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

const DisclosureTextControl: FC<{
  value: string
  readOnly: boolean
  onSave: (value: string) => void
}> = ({ value, readOnly, onSave }) => {
  const [text, setText] = useState(value)
  if (readOnly) {
    return <div className='paragraph-medium zocks-text-gray-700 zocks-px-0.75 py-2 rounded-3 zocks-bg-color-gray-100'>{value}</div>
  }
  return (
    <Form.Control
      as='textarea'
      rows={3}
      value={text}
      placeholder='For example: This meeting may be noted for compliance purposes.'
      aria-label='Disclosure text'
      onChange={(event) => setText(event.currentTarget.value)}
      onBlur={() => {
        if (text !== value) {
          onSave(text)
        }
      }}
    />
  )
}

export const PublicBookingPageSection: FC<SectionProps> = ({ settings, readOnly, isSaving = false, onUpdate }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: account } = useGetAccountQuery({ accountId })
  const logoFileId = account?.data.effectiveConfig.assets?.logo?.logoFile?.fileId
  const { data: logoDownload } = useDownloadGlobalFileRequestQuery(logoFileId ? { accountId, fileId: logoFileId } : skipToken)
  const logoUrl = logoDownload?.data.location
  const isLogoMissing = account != null && !logoFileId
  const publicBookingPage = settings.publicBookingPage
  const accountSlug = publicBookingPage.accountSlug
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId })
  const accountEventTypes = (eventTypes ?? []).filter((eventType) => eventType.access === EventTypeAccess.ACCOUNT)
  // Mirror the backend's client-facing resolution: IAM branding wins over the account name /
  // the legacy scheduling-settings brand color.
  const branding = account?.data.effectiveConfig.branding
  const firmName = branding?.displayName ?? account?.data.name ?? ''
  const brandColor = branding?.brandColor ?? publicBookingPage.branding.brandColor

  useEffect(() => {
    if (!accountSlug) {
      Sentry.captureException(new Error('Scheduling: account is missing accountSlug; cannot build a booking URL in the public booking page section'))
    }
  }, [accountSlug])

  const accountBookingEnabled = Boolean(publicBookingPage.accountBookingEnabled)
  const accountPageUrl = accountSlug ? `${BOOKING_BASE_URL}/${accountSlug}` : null
  const accountPageHref = accountPageUrl ? `https://${accountPageUrl}` : ''
  const banner = accountPageBanner(accountEventTypes, accountBookingEnabled)
  const previewButtonClassName = useButtonClassName('grayButton', 'd-flex align-items-center zocks-gap-0.5 text-decoration-none')
  const liveCount = accountPageLiveCount(accountEventTypes, accountBookingEnabled)
  const [turnOffOpen, setTurnOffOpen] = useState(false)

  const handleCopyAccountLink = async () => {
    try {
      await navigator.clipboard.writeText(accountPageHref)
    } catch (error) {
      Sentry.captureException(error)
      dispatch(addToast({ variant: 'error', title: 'Could not copy the link', messages: ['Please try again.'] }))
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Account booking page link copied', messages: ['Paste it anywhere to share with clients.'] }))
  }

  const setAccountBooking = (enabled: boolean) => onUpdate({ publicBookingPage: { ...publicBookingPage, accountBookingEnabled: enabled } })
  const showLogo = publicBookingPage.showLogo
  const compliance = settings.compliance
  const hasDisclosureText = Boolean(compliance.disclosureText)
  const [disclosureDraftOpen, setDisclosureDraftOpen] = useState(false)
  const disclosureEnabled = hasDisclosureText || disclosureDraftOpen

  const { userConsentTrackingEnabled } = useConsentManualConfigs()
  const consent: ConsentSettings = compliance.consent ?? { enabled: false }
  const consentLocked = !userConsentTrackingEnabled
  const updateConsent = (patch: Partial<ConsentSettings>) => onUpdate({ compliance: { ...compliance, consent: { ...consent, ...patch } } })

  return (
    <SettingsSection title='Public booking pages' subtitle='Where clients book meetings with you.' isSaving={isSaving}>
      <h6 className='mb-0 zocks-text-gray-900'>Booking pages</h6>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column gap-3'>
          <SettingsToggle
            label='Account booking page'
            description="One page for everyone in your account. Clients pick a time and Zocks assigns whoever's available."
            checked={accountBookingEnabled}
            readOnly={readOnly}
            ariaLabel='Account booking page'
            onChange={(checked) => {
              if (checked) {
                setAccountBooking(true)
              } else {
                setTurnOffOpen(true)
              }
            }}
          />
          {accountBookingEnabled ? (
            <div className='d-flex flex-column gap-2 p-3 border rounded-3 zocks-border-color-gray-200'>
              {accountPageUrl ? (
                <>
                  <div className='d-flex flex-wrap align-items-center justify-content-between gap-2'>
                    <span className='d-flex align-items-center zocks-gap-0.5 paragraph-medium zocks-text-gray-900'>
                      {accountPageUrl}
                      <InfoTooltip title={ACCOUNT_PAGE_URL_HINT} label='About the account booking page URL' />
                    </span>
                    <span className='d-flex align-items-center gap-2'>
                      <Button variant='grayButton' className='d-flex align-items-center zocks-gap-0.5' onClick={() => void handleCopyAccountLink()}>
                        <i className='bi bi-copy' aria-hidden='true' />
                        Copy link
                      </Button>
                      <a href={accountPageHref} target='_blank' rel='noopener noreferrer' className={previewButtonClassName}>
                        <i className='bi bi-box-arrow-up-right' aria-hidden='true' />
                        Preview page
                      </a>
                    </span>
                  </div>
                  {eventTypes ? <AccountPageStatus banner={banner} liveCount={liveCount} readOnly={readOnly} /> : null}
                </>
              ) : (
                <span className='paragraph-small zocks-text-danger'>Booking URL unavailable</span>
              )}
            </div>
          ) : null}
        </SettingsRow>
        <SettingsRow className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Individual booking pages</span>
            <span className='paragraph-small zocks-text-gray-600'>
              Each person in your account has their own page. They choose whether clients can find it.
            </span>
          </div>
          <div className='d-flex align-items-center zocks-gap-0.25 p-3 border rounded-3 zocks-border-color-gray-200 paragraph-medium zocks-text-gray-900'>
            {accountSlug ? (
              <>
                {`${BOOKING_BASE_URL}/${accountSlug}/`}
                <span className='px-1 border rounded-1 zocks-border-color-gray-300 zocks-text-gray-600'>user-name</span>
              </>
            ) : (
              <span className='paragraph-small zocks-text-danger'>Booking URL unavailable</span>
            )}
          </div>
        </SettingsRow>
      </SettingsCard>
      {turnOffOpen ? (
        <ConfirmDialog
          title='Turn off the account booking page?'
          body='Clients can no longer book on the account booking page. Your event type settings and host lists are kept, and existing bookings keep their scheduled times.'
          confirmLabel='Turn off'
          confirmVariant='primary'
          onConfirm={() => {
            setTurnOffOpen(false)
            setAccountBooking(false)
          }}
          onCancel={() => setTurnOffOpen(false)}
        />
      ) : null}

      <div className='d-flex flex-column'>
        <h6 className='mb-0 zocks-text-gray-900'>Brand</h6>
        <span className='paragraph-small zocks-text-gray-700'>
          Choose what clients see on your booking page. Change in{' '}
          <NavLink className='zocks-text-primary text-decoration-underline' to='/workspace/setup/settings/branding'>
            Branding
          </NavLink>
          .
        </span>
      </div>
      <SettingsCard>
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <span className='bold'>Brand color</span>
          <span className='d-flex align-items-center gap-2 paragraph-medium zocks-text-gray-900 zocks-bg-color-gray-100 rounded-1 ps-2 zocks-pe-0.75 py-2'>
            <span
              className={classNames(styles.brandColorSwatch, 'd-inline-block border zocks-border-color-gray-300')}
              style={{ backgroundColor: brandColor }}
            />
            {brandColorName(brandColor)} ({brandColor.toUpperCase()})
          </span>
        </SettingsRow>
        <SettingsRow className='d-flex flex-column zocks-gap-0.75'>
          <SettingsToggle
            label='Show logo'
            checked={showLogo}
            readOnly={readOnly}
            ariaLabel='Show logo'
            onChange={(checked) => onUpdate({ publicBookingPage: { ...publicBookingPage, showLogo: checked } })}
          />
          {showLogo && isLogoMissing ? <StatusBanner tone='warning'>No logo uploaded. Clients won&apos;t see a logo on your booking page.</StatusBanner> : null}
          {showLogo && !isLogoMissing ? (
            <div className='d-flex align-items-center justify-content-between gap-3 ps-3 pe-1 py-1 rounded-3 zocks-bg-color-gray-100'>
              <PreviewLabel />
              {logoUrl ? (
                <img src={logoUrl} alt='Account logo' className={classNames(styles.logoPreview, 'rounded-1 flex-shrink-0 object-fit-contain')} />
              ) : (
                <span className={`${styles.logoPreview} d-inline-block rounded-1 zocks-bg-color-gray-200 flex-shrink-0`} />
              )}
            </div>
          ) : null}
        </SettingsRow>
        <SettingsRow className='d-flex flex-column zocks-gap-0.75'>
          <SettingsToggle
            label='Show firm name'
            description='Firm name always appears in emails sent to clients.'
            checked={publicBookingPage.showAccountName}
            readOnly={readOnly}
            ariaLabel='Show firm name'
            onChange={(checked) => onUpdate({ publicBookingPage: { ...publicBookingPage, showAccountName: checked } })}
          />
          {publicBookingPage.showAccountName && firmName ? (
            <div className='d-flex flex-column zocks-gap-0.25 px-3 py-2 rounded-3 zocks-bg-color-gray-100'>
              <PreviewLabel showStatus={false} />
              <span className='paragraph-small zocks-text-gray-900 ps-4'>{firmName}</span>
            </div>
          ) : null}
        </SettingsRow>
        <SettingsRow>
          <SettingsToggle
            label="Show each user's photo"
            description='Each user can override this.'
            checked={publicBookingPage.showProfilePhotos}
            readOnly={readOnly}
            ariaLabel="Show each user's photo"
            onChange={(showProfilePhotos) => onUpdate({ publicBookingPage: { ...publicBookingPage, showProfilePhotos } })}
          />
        </SettingsRow>
      </SettingsCard>
      <div className='d-flex align-items-center zocks-gap-0.5'>
        <i className='bi bi-info-circle zocks-text-gray-700 flex-shrink-0' aria-hidden='true' />
        <span className='paragraph-small zocks-text-gray-700'>Show either the logo or the firm name. Your firm may need to identify itself to clients.</span>
      </div>

      <h6 className='mb-0 zocks-text-gray-900'>Compliance</h6>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column zocks-gap-0.5'>
          <SettingsToggle
            label='Disclosure text'
            description='Shown on the booking page before clients confirm. Often used for compliance disclosures.'
            checked={disclosureEnabled}
            readOnly={readOnly}
            ariaLabel='Enable disclosure text'
            onChange={(checked) => {
              setDisclosureDraftOpen(checked)
              if (!checked && hasDisclosureText) {
                onUpdate({ compliance: { ...compliance, disclosureText: '' } })
              }
            }}
          />
          {disclosureEnabled ? (
            <DisclosureTextControl
              value={compliance.disclosureText}
              readOnly={readOnly}
              onSave={(disclosureText) => onUpdate({ compliance: { ...compliance, disclosureText } })}
            />
          ) : null}
        </SettingsRow>
        <SettingsRow className={classNames('d-flex flex-column zocks-gap-0.75', consentLocked ? 'zocks-bg-color-gray-100' : undefined)}>
          <SettingsToggle
            label='Show consent checkbox'
            description='Ask clients to accept a consent message when they book.'
            checked={consent.enabled && !consentLocked}
            readOnly={readOnly || consentLocked}
            ariaLabel='Show consent checkbox'
            onChange={(enabled) => updateConsent({ enabled })}
          />
          <div
            className={classNames(
              'd-flex align-items-start zocks-gap-0.5 zocks-px-0.75 py-3 rounded-3',
              consentLocked ? 'zocks-bg-color-gray-200' : 'zocks-bg-color-gray-100',
            )}
          >
            <i className='bi bi-info-circle zocks-text-gray-700 flex-shrink-0' aria-hidden='true' />
            <span className='paragraph-small zocks-text-gray-700'>
              {consentLocked
                ? 'Consent lets your AI Assistant transcribe and analyze meetings. Turn on client consent tracking to enable this. '
                : "Consent lets your AI Assistant transcribe and analyze meetings. Answers given at booking are saved to the client's consent history. "}
              <NavLink to='/workspace/setup/settings/compliance' className='zocks-text-primary text-decoration-underline'>
                Manage consent tracking
              </NavLink>
            </span>
          </div>
        </SettingsRow>
        {consent.enabled && !consentLocked ? (
          <>
            <SettingsRow className='d-flex flex-column'>
              <div className='d-flex align-items-center zocks-gap-1'>
                <ChevronBullet className='flex-shrink-0' aria-hidden='true' />
                <span className='bold label-small'>Consent message</span>
              </div>
              <div className='d-flex flex-column zocks-gap-0.75 zocks-pl-2.0'>
                <span className='paragraph-small zocks-text-gray-600'>Shown next to the checkbox when clients book. Edit it or keep the default text.</span>
                <ConsentMessageEditor savedText={consent.text} readOnly={readOnly} onSave={(text) => updateConsent({ text })} />
              </div>
            </SettingsRow>
          </>
        ) : null}
      </SettingsCard>
    </SettingsSection>
  )
}
